<template>
  <tr
    class="hover:bg-gray-50 transition-colors duration-150 ease-in-out child:px-2 child:py-4"
    v-bind="$attrs"
  >
    <td
      v-if="showPackageDetails"
      class=""
    >
      <div class="flex items-center space-x-2">
        <div class="flex items-center space-x-1 flex-shrink-0">
          <RouterLink
            :to="{ name: 'PackagesDashboard', params: { projectId: $route.params.projectId, packageId: subcontractorPackage.id } }"
            class="btn-icon btn-icon-blue block"
            title="View Package"
            @click.stop=""
          >
            <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
          </RouterLink>
        </div>

        <div
          v-if="nextAction.timeBar"
          class="flex items-center"
          :title="nextAction.timeBar.text"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"
            :class="nextAction.timeBar.iconClass">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>
        </div>

        <div class="flex-1 min-w-0 text-sm">
          <p
            v-if="subcontractorPackage.subcontractor?.name"
            class="text-xs text-gray-500 w-60 text-wrap"
          >
            {{ subcontractorPackage.subcontractor.name }}
          </p>

          <p class="font-medium text-gray-900 w-60 text-wrap">
            {{ subcontractorPackage.name }}
          </p>

          <div class="flex items-center gap-2 text-xs text-gray-500">
            {{ australianCurrency(subcontractorPackage.totalAwarded) }}

            <span
              v-if="subcontractorPackage.procurement_method"
              class="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 capitalize"
            >
              {{ subcontractorPackage.procurement_method.replace(/_/g, ' ') }}
            </span>
          </div>
        </div>
      </div>
    </td>

    <td
      v-if="!claimOnly"
      class=" whitespace-nowrap text-right text-sm"
    >
      <template v-if="scopeGap">
        <p class="text-gray-900">
          {{ australianCurrency(scopeGapTotalForecast) }}
        </p>

        <p class="text-xs text-gray-500">
          {{ australianCurrency(scopeGapTotalFinalAmount) }}
        </p>
      </template>
    </td>

    <td
      v-if="!claimOnly"
      class=" whitespace-nowrap text-right text-sm text-gray-900"
    >
      {{ australianCurrency(approvedVariation) }}
    </td>

    <td
      v-if="!claimOnly"
      class=" whitespace-nowrap text-right text-sm text-gray-900"
    >
      {{ australianCurrency(finalAccount) }}
    </td>

    <td
      v-if="!claimOnly"
      class=" whitespace-nowrap text-right text-sm text-gray-900"
    >
      {{ australianCurrency(finalForecast) }}
    </td>

    <td class="">
      <div
        v-if="activeClaim"
        class="flex flex-col items-start gap-1"
      >
        <div class="flex items-center gap-2">
          <p class="text-sm font-medium text-gray-900 truncate">
            {{ $t('HeadContract.ProgressClaimsIndexLite.claimNumber', { claimNumber: activeClaim.claim_number }) }}
          </p>

          <span
            class="px-2 py-1 text-xs rounded-full text-nowrap"
            :class="claimStatusClass"
          >
            {{ ClaimStatusTitles[enumKeyFromValue(ClaimStatus, activeClaim.status)] }}
          </span>
        </div>

        <p
          v-if="createdOrIssuedAt"
          class="text-xs"
        >
          <span class="text-gray-500">{{ liteMode ? 'Created' : 'Issued' }}: </span>

          <span class="text-gray-900">{{ createdOrIssuedAt.toLocaleString() }}</span>
        </p>

        <p
          v-if="activeClaim.claimIssuedAt"
          class="text-xs"
        >
          <span class="text-gray-500">Submitted: </span>

          <span class="text-gray-900">{{ activeClaim.claimIssuedAt.toLocaleString() }}</span>
        </p>

        <p
          v-if="activeClaim.submittedAt"
          class="text-xs"
        >
          <span class="text-gray-500">Certified: </span>

          <span class="text-gray-900">{{ activeClaim.submittedAt.toLocaleString() }}</span>
        </p>
      </div>

      <div
        v-else
        class="flex items-center space-x-2"
      >
        <span class="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
          No claim
        </span>
      </div>
    </td>

    <td class=" whitespace-nowrap text-sm text-gray-900">
      <i18n-t
        v-if="activeClaim"
        keypath="HeadContract.ProgressClaimsIndexLite.claimPeriod"
        tag="div"
        class="text-gray-500"
      >
        <template #startDate>
          <div class="text-gray-900">
            {{ new Date(activeClaim.start).toDateString() }}
          </div>
        </template>

        <template #endDate>
          <span class="text-gray-900">
            {{ new Date(activeClaim.end).toDateString() }}
          </span>
        </template>
      </i18n-t>
    </td>

    <td class="">
      <p
        v-if="nextAction.nextActionText"
        class="text-sm text-wrap w-56 font-medium"
        :class="nextAction.timeBar?.textClass ?? 'text-gray-900'"
        :title="nextAction.nextActionText"
      >
        {{ nextAction.nextActionText }}
      </p>
    </td>

    <td class=" whitespace-nowrap text-right">
      <div
        v-if="activeClaim"
        class="flex flex-col items-end gap-0.5 text-sm"
      >
        <div>
          <span class="text-gray-500">Claim: </span>

          <span class="text-gray-900">{{ $tcc(activeClaim.totalClaim) }}</span>
        </div>

        <div>
          <span class="text-gray-500">Certified: </span>

          <span class="text-gray-900">{{ $tcc(activeClaim.totalCertified) }}</span>
        </div>

        <div>
          <span class="text-gray-500">Variance: </span>

          <span :class="activeClaim.totalVariance !== 0 ? 'text-red-600' : 'text-gray-900'">
            {{ $tcc(activeClaim.totalVariance) }}
          </span>
        </div>
      </div>
    </td>

    <td class=" whitespace-nowrap text-right">
      <div
        v-if="latestClaim"
        class="flex flex-col items-end gap-0.5 text-sm"
      >
        <div>
          <span class="text-gray-500">Claim: </span>

          <span class="text-gray-900">{{ $tcc(latestClaim.cumulativeClaim) }}</span>
        </div>

        <div>
          <span class="text-gray-500">Certified: </span>

          <span class="text-gray-900">{{ $tcc(latestClaim.cumulativeCertified) }}</span>
        </div>

        <div>
          <span class="text-gray-500">Variance: </span>

          <span :class="latestClaim.cumulativeVariance !== 0 ? 'text-red-600' : 'text-gray-900'">
            {{ $tcc(latestClaim.cumulativeVariance) }}
          </span>
        </div>
      </div>
    </td>

    <td class=" whitespace-nowrap text-right">
      <div
        class="flex flex-col justify-end space-y-2"
        @click.stop=""
      >
        <template v-if="projectsStore.project?.unarchived">
          <button
            v-if="!claimOnly && !activeClaim"
            type="button"
            class="btn btn--primary-blue btn--small"
            :disabled="creatingClaimForPackage"
            @click="createClaimForPackage(subcontractorPackage, activeClaim ?? latestClaimForPackage)"
          >
            <span>
              Create Claim
            </span>
          </button>

          <button
            v-if="!$props.liteMode && canIssueClaim(activeClaim)"
            type="button"
            class="btn btn--primary-green btn--small"
            :disabled="issuingClaimForPackage"
            @click="issueClaimForPackage(activeClaim)"
          >
            <span v-if="activeClaim.status === ClaimStatus.draft">
              Issue Claim
            </span>

            <span v-else-if="activeClaim.status === ClaimStatus.draft_subcontractor">
              Reissue Claim
            </span>
          </button>

          <RouterLink
            v-if="activeClaim && activeClaim.status === ClaimStatus.subcontractor_claim"
            :to="{
              name: 'SubcontractorClaimShow',
              params: {
                projectId: $route.params.projectId, packageId: subcontractorPackage.id, claimId: activeClaim.id
              }
            }"
            class="btn--secondary-blue btn btn--small text-center"
          >
            Add Certified Amounts
          </RouterLink>
        </template>

        <RouterLink
          v-if="activeClaim"
          :to="{
            name: 'SubcontractorClaimShow',
            params: {
              projectId: $route.params.projectId, packageId: subcontractorPackage.id, claimId: activeClaim.id
            }
          }"
          class="btn--secondary-blue btn btn--small text-center"
        >
          View Claim
        </RouterLink>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import {
  australianCurrency,
  enumKeyFromValue,
  getNextAction,
  getNextTimeBarDueIn,
  getNextTimeBarDueInBadgeClass,
} from '@/helpers';
import Claim, { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    subcontractorPackage: Package;
    projectSupplier: ProjectSupplier;
    liteMode?: boolean;
    selectedMonth: DateTime;
    showPackageDetails?: boolean;
    claim?: Claim;
  }>(),
  {
    showPackageDetails: false,
    liteMode: false,
    claim: undefined,
  },
);

const emits = defineEmits<{
  (e: 'createClaim', data: { projectSupplier: ProjectSupplier; packageData: Package; latestClaim?: Claim }): void;
  (e: 'issueClaim', data: { claim: Claim; email: string }): void;
}>();

defineOptions({
  inheritAttrs: false,
});

const issuingClaimForPackage = ref(false);
const creatingClaimForPackage = ref(false);
const claimsStore = useClaimsStore();
const projectsStore = useProjectsStore();

const latestClaimForMonth = computed(() => {
  return claimsStore.models
    .whereHas('package_claim', (query) => {
      query.where('package_id', props.subcontractorPackage.id)
        .where('project_supplier_id', props.projectSupplier.id);
    })
    .orderBy('claim_number', 'desc')
    .where('start', (start) => {
      return start >= props.selectedMonth.startOf('month').toISO() &&
        start <= props.selectedMonth.endOf('month').toISO();
    })
    .with('scope_gap')
    .first();
});

const claimOnly = computed(() => !!props.claim);
const activeClaim = computed(() => props.claim ?? latestClaimForMonth.value);

const latestClaimForPackage = computed(() => {
  return claimsStore.models
    .whereHas('package_claim', (query) => {
      query.where('package_id', props.subcontractorPackage.id)
        .where('project_supplier_id', props.projectSupplier.id);
    })
    .orderBy('claim_number', 'desc')
    .with('scope_gap')
    .first();
});

const latestClaim = computed(() => {
  return latestClaimForMonth.value ?? latestClaimForPackage.value;
});

const scopeGap = computed(() => activeClaim.value?.scope_gap ?? latestClaimForPackage.value?.scope_gap);
const scopeGapTotalForecast = computed(() => scopeGap.value?.totalForecast ?? 0);
const scopeGapTotalFinalAmount = computed(() => scopeGap.value?.totalFinalAmount ?? 0);

const finalAccount = computed(() => {
  return props.subcontractorPackage.totalAwarded + approvedVariation.value;
});

const approvedVariation = computed(() => props.subcontractorPackage.totalApprovedVariation);

const finalForecast = computed(() => {
  return scopeGapTotalFinalAmount.value +
    (activeClaim.value?.totalActualForecast ?? latestClaimForPackage.value?.totalActualForecast ?? 0);
});

const createClaimForPackage = (packageData: Package, latestClaim?: Claim) => {
  emits('createClaim', { projectSupplier: props.projectSupplier, packageData, latestClaim });
};

const canIssueClaim = (claim: Claim | undefined) => {
  if(!claim) return false;

  // Can issue if claim is in draft status and is for current month
  return (claim.status === ClaimStatus.draft || claim.status === ClaimStatus.draft_subcontractor);
};

const issueClaimForPackage = async (claim: Claim) => {
  if(!claim) return;

  issuingClaimForPackage.value = true;

  try {
    // Get supplier contact email
    const supplierEmail = props.projectSupplier.contact_email;

    // Show SweetAlert prompt for optional email override
    const result = await Swal.fire({
      title: 'Issue Claim',
      html: `
        <div class="text-left">
          <p class="mb-4">This claim will be sent to the subcontractor via email.</p>
          <p class="mb-4 text-sm text-gray-600">
            <strong>Default email:</strong> ${supplierEmail || 'No contact email set'}
          </p>
          <p class="mb-4 text-sm text-gray-600">
            You can override the email address below if needed.
          </p>
        </div>
        <input
          id="email-input"
          type="email"
          class="swal2-input"
          placeholder="Override email (optional)"
          value="${supplierEmail || ''}"
        />
      `,

      showCancelButton: true,
      confirmButtonText: 'Issue Claim',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const emailInput = document.getElementById('email-input') as HTMLInputElement;
        const email = emailInput?.value?.trim();

        // If no email provided and no default email, show error
        if(!email && !supplierEmail) {
          Swal.showValidationMessage('Please provide an email address');

          return false;
        }

        // If email provided, validate it
        if(email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          Swal.showValidationMessage('Please enter a valid email address');

          return false;
        }

        return email || supplierEmail;
      },

      allowOutsideClick: false,
      allowEscapeKey: true,
    });

    if(result.isConfirmed && result.value) {
      const emailToUse = result.value as string;

      // Call the API to issue the claim
      emits('issueClaim', { claim, email: emailToUse });
    }
  } catch (error) {
    console.error('Failed to issue claim:', error);

    await Swal.fire({
      title: 'Error',
      text: 'Failed to issue the claim. Please try again.',
      icon: 'error',
      confirmButtonColor: '#ef4444',
    });
  } finally {
    issuingClaimForPackage.value = false;
  }
};

const { project } = storeToRefs(projectsStore);
const nextAction = computed(() => getNextAction(activeClaim.value, project.value, props.selectedMonth));
const nextTimeBarDueIn = computed(() => getNextTimeBarDueIn(activeClaim.value, project.value, props.selectedMonth));
const claimStatusClass = computed(() => getNextTimeBarDueInBadgeClass(nextTimeBarDueIn.value, activeClaim.value));

const createdOrIssuedAt = computed(() => {
  if(!activeClaim.value) {
    return undefined;
  }

  return props.liteMode ?
    activeClaim.value.createdAt :
    activeClaim.value.draftIssuedAt;
});
</script>

<style scoped></style>
